import React, { useEffect, useState } from "react"
import API from "../../api/axios"
import AdminLayout from "./AdminLayout"

const EMPTY_COURSE = {
  title: "", slug: "", description: "", shortDesc: "", thumbnail: "",
  instructor: "Mr. Ali", level: "beginner", duration: "", price: 0,
  currency: "KES", isFree: false, isTrial: false, trialDays: 0,
  isPublished: false, isFeatured: false, category: "Forex Trading",
}
const EMPTY_MODULE = { title: "", orderIndex: 0 }
const EMPTY_LESSON = { title: "", description: "", videoUrl: "", videoType: "youtube", duration: 0, orderIndex: 0, isFree: false }

const AdminCourses = () => {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState("")
  const [success, setSuccess] = useState("")

  // Course modal
  const [showCourseModal, setShowCourseModal] = useState(false)
  const [editCourse, setEditCourse]           = useState(null)
  const [courseForm, setCourseForm]           = useState(EMPTY_COURSE)
  const [saving, setSaving]                   = useState(false)

  // Module/Lesson forms (inline, per course)
  const [addingModule, setAddingModule]     = useState(null) // courseId
  const [moduleForm, setModuleForm]         = useState(EMPTY_MODULE)
  const [addingLesson, setAddingLesson]     = useState(null) // { courseId, moduleId }
  const [lessonForm, setLessonForm]         = useState(EMPTY_LESSON)

  const fetchCourses = async () => {
    setLoading(true)
    try {
      const { data } = await API.get("/courses/admin/all")
      setCourses(data.data)
    } catch { setError("Failed to load courses.") }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchCourses() }, [])

  const openAdd    = () => { setCourseForm(EMPTY_COURSE); setEditCourse(null); setShowCourseModal(true) }
  const openEdit   = (c) => { setCourseForm({ ...c }); setEditCourse(c); setShowCourseModal(true) }
  const closeModal = () => { setShowCourseModal(false); setEditCourse(null) }

  const slugify = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")

  const saveCourse = async (e) => {
    e.preventDefault()
    setSaving(true); setError(""); setSuccess("")
    try {
      const payload = { ...courseForm }
      if (!payload.slug) payload.slug = slugify(payload.title)
      if (editCourse) {
        await API.put(`/courses/${editCourse.id}`, payload)
        setSuccess("Course updated.")
      } else {
        await API.post("/courses", payload)
        setSuccess("Course created.")
      }
      closeModal()
      fetchCourses()
    } catch (err) {
      setError(err.response?.data?.message || "Save failed.")
    } finally { setSaving(false) }
  }

  const deleteCourse = async (id) => {
    if (!window.confirm("Delete this course? This cannot be undone.")) return
    try {
      await API.delete(`/courses/${id}`)
      fetchCourses()
    } catch { setError("Delete failed.") }
  }

  const saveModule = async (courseId) => {
    try {
      await API.post(`/courses/${courseId}/modules`, moduleForm)
      setAddingModule(null); setModuleForm(EMPTY_MODULE); fetchCourses()
    } catch (err) { setError(err.response?.data?.message || "Failed.") }
  }

  const saveLesson = async (courseId, moduleId) => {
    try {
      await API.post(`/courses/${courseId}/modules/${moduleId}/lessons`, lessonForm)
      setAddingLesson(null); setLessonForm(EMPTY_LESSON); fetchCourses()
    } catch (err) { setError(err.response?.data?.message || "Failed.") }
  }

  return (
    <AdminLayout title="Course Manager">
      {error   && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      <div className="admin-section">
        <div className="admin-section-header">
          <h3>All Courses ({courses.length})</h3>
          <button className="btn-primary" onClick={openAdd}>
            <i className="fa fa-plus"></i> Add Course
          </button>
        </div>

        {loading ? (
          <div className="admin-empty"><i className="fa fa-spinner fa-spin"></i> Loading…</div>
        ) : courses.length === 0 ? (
          <div className="admin-empty">No courses yet. Add your first course.</div>
        ) : (
          courses.map(course => (
            <div key={course.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              {/* Course row */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "16px 20px" }}>
                {course.thumbnail && (
                  <img src={course.thumbnail} alt="" style={{ width: 64, height: 44, objectFit: "cover", borderRadius: 4 }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>{course.title}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>
                    {course.level} · {course.category} · KES {Number(course.price).toLocaleString()}
                    {course.isFree && " · FREE"}
                    {" · "}
                    <span className={`badge badge-${course.isPublished ? "active" : "read"}`}>
                      {course.isPublished ? "Published" : "Draft"}
                    </span>
                    {course.isFeatured && <span className="badge badge-pending" style={{ marginLeft: 4 }}>Featured</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                    {course.modules?.length || 0} modules · {course.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0)} lessons · {course.enrollCount} enrolled
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button className="btn-sm btn-edit"    onClick={() => openEdit(course)}>Edit</button>
                  <button className="btn-sm btn-approve" onClick={() => setAddingModule(course.id)}>+ Module</button>
                  <button className="btn-sm btn-danger"  onClick={() => deleteCourse(course.id)}>Delete</button>
                </div>
              </div>

              {/* Add module inline form */}
              {addingModule === course.id && (
                <div style={{ padding: "12px 20px 16px", background: "#f8f9fa", borderTop: "1px dashed #ddd" }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>New Module</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <input
                      className="admin-search" placeholder="Module title" value={moduleForm.title}
                      onChange={e => setModuleForm({ ...moduleForm, title: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <input
                      className="admin-search" type="number" placeholder="Order" value={moduleForm.orderIndex}
                      onChange={e => setModuleForm({ ...moduleForm, orderIndex: Number(e.target.value) })}
                      style={{ width: 70 }}
                    />
                    <button className="btn-primary btn-sm" onClick={() => saveModule(course.id)}>Save</button>
                    <button className="btn-sm btn-toggle" onClick={() => setAddingModule(null)}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Modules & Lessons */}
              {course.modules?.map(mod => (
                <div key={mod.id} style={{ background: "#faf8f3", paddingLeft: 32 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 20px 8px 0", borderTop: "1px dashed #e9e0c9" }}>
                    <i className="fa fa-folder" style={{ color: "#c9a227", fontSize: 12 }}></i>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#555", flex: 1 }}>{mod.title}</span>
                    <span style={{ fontSize: 11, color: "#aaa" }}>{mod.lessons?.length || 0} lessons</span>
                    <button className="btn-sm btn-approve" onClick={() => setAddingLesson({ courseId: course.id, moduleId: mod.id })}>
                      + Lesson
                    </button>
                  </div>

                  {/* Lesson list */}
                  {mod.lessons?.map(l => (
                    <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 20px 6px 24px", fontSize: 12, color: "#666" }}>
                      <i className={`fa fa-${l.isFree ? "unlock" : "lock"}`} style={{ color: l.isFree ? "#2e7d52" : "#c9a227", width: 12 }}></i>
                      <span style={{ flex: 1 }}>{l.title}</span>
                      <span className="badge badge-read">{l.videoType}</span>
                      {l.duration > 0 && <span style={{ color: "#aaa" }}>{Math.round(l.duration / 60)}m</span>}
                    </div>
                  ))}

                  {/* Add lesson inline */}
                  {addingLesson?.moduleId === mod.id && (
                    <div style={{ padding: "12px 20px 16px", background: "#f0ece0" }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>New Lesson</div>
                      <div className="form-row" style={{ marginBottom: 8 }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <input className="admin-search" placeholder="Lesson title" value={lessonForm.title}
                            onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })} style={{ width: "100%" }} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <input className="admin-search" placeholder="Video URL" value={lessonForm.videoUrl}
                            onChange={e => setLessonForm({ ...lessonForm, videoUrl: e.target.value })} style={{ width: "100%" }} />
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <select className="admin-select" value={lessonForm.videoType}
                          onChange={e => setLessonForm({ ...lessonForm, videoType: e.target.value })}>
                          <option value="youtube">YouTube</option>
                          <option value="vimeo">Vimeo</option>
                          <option value="upload">Upload</option>
                          <option value="live">Live</option>
                        </select>
                        <input className="admin-search" type="number" placeholder="Duration (sec)" value={lessonForm.duration}
                          onChange={e => setLessonForm({ ...lessonForm, duration: Number(e.target.value) })} style={{ width: 130 }} />
                        <label className="form-toggle">
                          <input type="checkbox" checked={lessonForm.isFree}
                            onChange={e => setLessonForm({ ...lessonForm, isFree: e.target.checked })} />
                          Free Preview
                        </label>
                        <button className="btn-primary btn-sm" onClick={() => saveLesson(course.id, mod.id)}>Save</button>
                        <button className="btn-sm btn-toggle" onClick={() => setAddingLesson(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Course Modal */}
      {showCourseModal && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editCourse ? "Edit Course" : "Add New Course"}</h3>
              <button className="admin-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={saveCourse}>
              <div className="admin-modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Title *</label>
                    <input value={courseForm.title} onChange={e => setCourseForm({ ...courseForm, title: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Slug (auto-generated)</label>
                    <input value={courseForm.slug} onChange={e => setCourseForm({ ...courseForm, slug: e.target.value })}
                      placeholder="leave blank to auto-generate" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <textarea value={courseForm.description} onChange={e => setCourseForm({ ...courseForm, description: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Short Description</label>
                  <input value={courseForm.shortDesc} onChange={e => setCourseForm({ ...courseForm, shortDesc: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Thumbnail URL</label>
                    <input value={courseForm.thumbnail} onChange={e => setCourseForm({ ...courseForm, thumbnail: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Instructor</label>
                    <input value={courseForm.instructor} onChange={e => setCourseForm({ ...courseForm, instructor: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Level</label>
                    <select value={courseForm.level} onChange={e => setCourseForm({ ...courseForm, level: e.target.value })}>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="all">All Levels</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Duration (e.g. "4 weeks")</label>
                    <input value={courseForm.duration} onChange={e => setCourseForm({ ...courseForm, duration: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Price (KES)</label>
                    <input type="number" min="0" step="0.01" value={courseForm.price}
                      onChange={e => setCourseForm({ ...courseForm, price: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <input value={courseForm.category} onChange={e => setCourseForm({ ...courseForm, category: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 6 }}>
                  <label className="form-toggle">
                    <input type="checkbox" checked={courseForm.isFree} onChange={e => setCourseForm({ ...courseForm, isFree: e.target.checked })} />
                    Free Course
                  </label>
                  <label className="form-toggle">
                    <input type="checkbox" checked={courseForm.isTrial} onChange={e => setCourseForm({ ...courseForm, isTrial: e.target.checked })} />
                    Free Trial
                  </label>
                  {courseForm.isTrial && (
                    <div className="form-group" style={{ margin: 0, alignSelf: "center" }}>
                      <input
                        type="number" min="0" placeholder="Trial days (0=unlimited)"
                        value={courseForm.trialDays}
                        onChange={e => setCourseForm({ ...courseForm, trialDays: Number(e.target.value) })}
                        style={{ width: 160 }}
                      />
                    </div>
                  )}
                  <label className="form-toggle">
                    <input type="checkbox" checked={courseForm.isPublished} onChange={e => setCourseForm({ ...courseForm, isPublished: e.target.checked })} />
                    Published
                  </label>
                  <label className="form-toggle">
                    <input type="checkbox" checked={courseForm.isFeatured} onChange={e => setCourseForm({ ...courseForm, isFeatured: e.target.checked })} />
                    Featured
                  </label>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="btn-sm btn-toggle" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <><i className="fa fa-spinner fa-spin"></i> Saving…</> : "Save Course"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminCourses
